import { createTheme } from '@mui/material/styles';
import { pickersSectionListClasses } from '@mui/x-date-pickers/PickersSectionList';
import { pickersOutlinedInputClasses } from '@mui/x-date-pickers/PickersTextField';

import { colors } from './colors';
import { gradients } from './gradients';

const inputPadding = '10px 12px';

const optionStyles = {
  padding: '10px 12px',
  '&:hover, &.Mui-focusVisible': {
    backgroundColor: colors.white.hover,
  },
  '&:not(:first-child)': {
    borderTop: `1px solid ${colors.gray.primary}`,
  },
  '&.Mui-selected': {
    backgroundColor: colors.white.active,
    '&:hover': {
      backgroundColor: colors.white.hover,
    },
  },
};

const theme = createTheme({
  cssVariables: true,
  palette: {
    ...colors,
    gradients,
    text: {
      primary: colors.black.primary,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
  typography: {
    fontSize: 14,
    fontFamily: 'Manrope',
    caption: {
      fontSize: '12px',
      lineHeight: '16px',
    },
    h2: {
      fontWeight: 700,
      fontSize: '18px',
      lineHeight: '28px',
    },
    h1: {
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: '32px',
    },
    h3: {
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: '22px',
    },
    body2: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '22px',
    },
    body1: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '22px',
    },
  },
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: optionStyles,
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '0px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        startIcon: {
          marginLeft: 0,
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          padding: inputPadding,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        input: {
          padding: inputPadding,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          top: 10,
          right: 12,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          '&.MuiMenu-list': {
            padding: 0,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          width: 4,
          height: 4,
          background: gradients.accentGradient.primary.primary,
        },
      },
    },
    // @ts-ignore
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            background: gradients.accentGradient.primary.primary,
          },
        },
      },
    },
    // @ts-ignore
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          '& .MuiYearCalendar-button.Mui-selected': {
            background: gradients.accentGradient.primary.primary,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          gap: '4px',
          width: '100%',
          padding: '12px',
          marginLeft: 'unset',
          marginRight: 'unset',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            margin: '0 4px',
            fontSize: '12px',
            lineHeight: '16px',
            color: colors.red.primary.primary,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          padding: '12px',
          fontSize: '12px',
          lineHeight: '16px',
          borderRadius: '8px',
          color: colors.black.primary,
          background: colors.white.primary,
          border: `1.5px solid ${colors.gray.primary}`,
          boxShadow: `0px 16px 32px 0px ${colors.greendark.shadow8}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          margin: 16,
          borderRadius: '16px',
          maxHeight: 'calc(100% - 32px)',
          background: colors.white.primary,
          boxShadow: `0px 16px 32px 0px ${colors.greendark.shadow8}`,
          [theme.breakpoints.up('md')]: {
            margin: 24,
            maxHeight: 'calc(100% - 48px)',
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.MuiMenu-paper': {
            padding: '2px',
            display: 'flex',
            boxShadow: 'none',
            overflow: 'hidden',
            borderRadius: '8px',
            background: gradients.accentGradient.primary.primary,
            '.MuiList-root': {
              width: '100%',
              maxHeight: '100%',
              overflowY: 'auto',
              borderRadius: '7px',
              background: colors.white.primary,
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            height: 22,
            borderRadius: '7px',
            padding: inputPadding,
            background: colors.white.primary,

            '&::placeholder': {
              opacity: 1,
              color: colors.graydark.primary,
            },
            '&::-moz-placeholder': {
              opacity: 1,
              color: colors.graydark.primary,
            },
            '&:-ms-input-placeholder': {
              opacity: 1,
              color: colors.graydark.primary,
            },
            '&::-webkit-input-placeholder': {
              opacity: 1,
              color: colors.graydark.primary,
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiInputAdornment-positionEnd': {
            transform: 'rotate(180deg)',
          },
        },
        inputRoot: {
          padding: '1px !important',
          '.MuiInputAdornment-positionEnd': {
            top: 10,
            right: '12px !important',
          },
          '.MuiOutlinedInput-input': {
            borderRadius: 7,
            cursor: 'pointer',
            padding: inputPadding,
            paddingRight: '48px !important',
          },
        },
        paper: {
          padding: '2px',
          boxShadow: 'none',
          borderRadius: '8px',
          background: gradients.accentGradient.primary.primary,
          '.MuiAutocomplete-listbox': {
            padding: 0,
          },
          '.MuiAutocomplete-noOptions': {
            padding: '10px 12px',
          },
          '.MuiAutocomplete-listbox, .MuiAutocomplete-noOptions': {
            width: '100%',
            borderRadius: '7px',
            background: colors.white.primary,
            '.MuiAutocomplete-option': optionStyles,
          },
        },
      },
    },

    // @ts-ignore
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          padding: '1px',
          borderRadius: '8px',
          background: colors.gray.primary,

          '&.Mui-error': {
            background: colors.red.primary.primary,
          },

          [`& .${pickersOutlinedInputClasses.notchedOutline}`]: {
            display: 'none',
          },

          [`.${pickersOutlinedInputClasses.input}`]: {
            backgroundColor: colors.white.hover,
          },

          '&.Mui-focused, &.Mui-focused:hover': {
            background: gradients.accentGradient.primary.primary,
          },

          [`& .${pickersSectionListClasses.root}`]: {
            opacity: 'unset',
            borderRadius: '7px',
            padding: '10px 12px',
            background: colors.white.primary,
          },

          [`& .${pickersOutlinedInputClasses.input}`]: {
            height: '22px',
            borderRadius: '7px',
            padding: inputPadding,
            background: colors.white.primary,
          },

          '&:hover:not(.Mui-focused)': {
            background: colors.gray.hover,
            [`& .${pickersSectionListClasses.root}`]: {
              background: colors.white.hover,
            },
          },

          '& .MuiInputAdornment-root': {
            margin: 0,
            top: '50%',
            padding: 0,
            right: '14px',
            position: 'absolute',
            transform: 'translateY(-50%)',
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          padding: inputPadding,
          '&.MuiSelect-select': {
            borderRadius: '7px',
            paddingRight: '48px !important',
          },
        },
        root: {
          padding: '1px',
          borderRadius: '8px',
          background: colors.gray.primary,
          '&:hover': {
            background: colors.gray.hover,
          },
          '&.MuiInputBase-root': {
            '& .MuiOutlinedInput-notchedOutline': {
              display: 'none',
            },
            '&:hover .MuiOutlinedInput-input': {
              backgroundColor: colors.white.hover,
            },
            '& input:-webkit-autofill': {
              WebkitTextFillColor: colors.black.primary,
              WebkitBoxShadow: `0 0 0 1000px ${colors.white.primary} inset`,
            },
            '&.Mui-error': {
              background: colors.red.primary.primary,

              '.MuiOutlinedInput-input': {
                backgroundColor: colors.white.primary,
              },
            },
            '&.Mui-focused': {
              background: gradients.accentGradient.primary.primary,

              '.MuiOutlinedInput-input': {
                backgroundColor: colors.white.primary,
              },
            },
            '&.MuiInputBase-adornedEnd': {
              '.MuiOutlinedInput-input': {
                paddingRight: 40,
              },
              '.MuiInputAdornment-positionEnd': {
                top: 10,
                width: 24,
                right: 10,
                height: 24,
                display: 'flex',
                position: 'absolute',
              },
            },
            '&.Mui-disabled': {
              background: colors.gray.primary,
              '&:hover': {
                background: colors.gray.primary,
              },
              '&:hover .MuiOutlinedInput-input': {
                backgroundColor: colors.white.primary,
              },
              '.MuiOutlinedInput-input': {
                color: colors.graydark.primary,
                backgroundColor: colors.white.primary,
              },
            },
          },
        },
      },
    },
  },
});

export { colors, gradients };
export default theme;
